/**
 * 🚗 HOST VEHICLE DASHBOARD
 * Complete dashboard for managing host vehicle applications
 * Includes statistics, filtering, and detailed review capabilities
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Chip,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  DirectionsCar,
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  TrendingUp,
  TrendingDown,
  MoreVert,
  Download,
  Refresh,
  Assessment,
  DriveEta,
  Star,
  Verified,
  LocalGasStation
} from '@mui/icons-material';
import { hostVehicleService, type HostVehicleStats } from '@/services/hostVehicleService';
import HostVehicleApplicationsList from '@/components/hostVehicles/HostVehicleApplicationsList';
import HostVehicleDetailViewer from '@/components/hostVehicles/HostVehicleDetailViewer';

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info';
  trend?: number;
  subtitle?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color, trend, subtitle }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" color={color} fontWeight="bold">
              {value.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend > 0 ? (
                  <TrendingUp color="success" fontSize="small" />
                ) : (
                  <TrendingDown color="error" fontSize="small" />
                )}
                <Typography 
                  variant="caption" 
                  color={trend > 0 ? 'success.main' : 'error.main'}
                  sx={{ ml: 0.5 }}
                >
                  {Math.abs(trend)}% vs last period
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Box 
              sx={{ 
                color: `${color}.main`,
                fontSize: 40,
                mb: 1
              }}
            >
              {icon}
            </Box>
            <IconButton 
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => setAnchorEl(null)}>
                <ListItemIcon>
                  <Assessment />
                </ListItemIcon>
                <ListItemText>View Details</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>
                <ListItemIcon>
                  <Download />
                </ListItemIcon>
                <ListItemText>Export Data</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const HostVehicleDashboard: React.FC = () => {
  const [stats, setStats] = useState<HostVehicleStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load statistics
  const loadStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await hostVehicleService.getHostVehicleStats();
      
      if (result.success && result.stats) {
        setStats(result.stats);
      } else {
        setError('Failed to load statistics');
      }
    } catch (err) {
      console.error('Error loading host vehicle stats:', err);
      setError('Error loading statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatsUpdate = (newStats: HostVehicleStats) => {
    setStats(newStats);
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Host Vehicle Management
        </Typography>
        <Alert severity="info">Loading dashboard...</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom>
          Host Vehicle Management
        </Typography>
        <Alert severity="error" action={
          <Button size="small" onClick={loadStats}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Host Vehicle Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and manage vehicle hosting applications
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<Refresh />}
            onClick={loadStats}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            startIcon={<Download />}
            variant="outlined"
          >
            Export Report
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Applications"
              value={stats.total}
              icon={<DirectionsCar />}
              color="primary"
              trend={12}
              subtitle="All time submissions"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Pending Review"
              value={stats.pending}
              icon={<Schedule />}
              color="warning"
              subtitle="Awaiting admin review"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Approved Vehicles"
              value={stats.approved}
              icon={<CheckCircle />}
              color="success"
              trend={8}
              subtitle="Active host vehicles"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Rejected"
              value={stats.rejected}
              icon={<Cancel />}
              color="error"
              subtitle="Applications declined"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Recent Applications"
              value={stats.recentApplications}
              icon={<TrendingUp />}
              color="info"
              subtitle="Last 7 days"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Avg Review Time"
              value={Math.round(stats.averageReviewTime)}
              icon={<Assessment />}
              color="primary"
              subtitle="Hours to review"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Breakdown
                </Typography>
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip icon={<DirectionsCar />} label="Economy" size="small" />
                    <Typography variant="body2">45%</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip icon={<Star />} label="Premium" color="primary" size="small" />
                    <Typography variant="body2">30%</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip icon={<DriveEta />} label="SUV" color="success" size="small" />
                    <Typography variant="body2">15%</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip icon={<LocalGasStation />} label="Electric" color="info" size="small" />
                    <Typography variant="body2">10%</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={1}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    startIcon={<CheckCircle />}
                  >
                    Bulk Approve
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    startIcon={<Download />}
                  >
                    Export Pending
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    fullWidth
                    startIcon={<Assessment />}
                  >
                    Generate Report
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Applications List */}
      <Card>
        <CardContent>
          <HostVehicleApplicationsList onStatsUpdate={handleStatsUpdate} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default HostVehicleDashboard;